'use client';

import React, { useState, useMemo } from 'react';
import {
    Check,
    ChevronRight,
    ChevronLeft,
    Building2,
    FileText,
    User,
    MapPin,
    Phone,
    Mail,
    Globe,
    Briefcase,
    Upload,
    Calendar,
    AlertCircle,
    Loader2,
    X,
    UserPlus,
    BookCopy,
    Facebook,
    Trash2,
    ArrowLeft as ArrowLeftIcon,
    ArrowRight as ArrowRightIcon,
    ImagePlus,
    Camera,
    Search,
    RotateCw,
    CheckCircle2,
    Info
} from 'lucide-react';
import { AvatarCropperModal } from '@/doi-tac-module/components/profile/AvatarCropperModal';
import { AvatarHistoryModal } from '@/doi-tac-module/components/profile/AvatarHistoryModal';
import { useAgencyImageUpload, AgencyImageField } from '@/doi-tac-module/hooks/use-agency-image-upload';
import { AgencySharedImageField } from './agency-shared-image-field';
import { 
    Dialog, 
    DialogContent, 
    DialogHeader, 
    DialogTitle 
} from "@/components/ui/dialog";
import { 
    DropdownMenu, 
    DropdownMenuTrigger, 
    DropdownMenuContent, 
    DropdownMenuItem 
} from "@/components/ui/dropdown-menu";
import imageCompression from 'browser-image-compression';
import { getPresignedUploadUrl } from '@/doi-tac-module/actions/s3';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuth } from '@/doi-tac-module/contexts/AuthContext';
import { ImageGalleryLightbox } from '@/doi-tac-module/components/common/image-gallery-lightbox';
import { AgencyProfile, S3File } from '@/doi-tac-module/lib/types';
import { submitAgencyRegistration } from '@/doi-tac-module/actions/agency-actions';
import { useToast } from '@/doi-tac-module/hooks/use-toast';
import { cn } from '@/doi-tac-module/lib/utils';
import { useRouter } from 'next/navigation';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { allIndustries } from '@/doi-tac-module/lib/industry-data';
import { industryTranslations } from '@/doi-tac-module/constants/industry-translations';
import { 
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";


const PHONE_CHAR_REGEX = /^[0-9+#\-\s]*$/;

interface AgencyRegistrationFormProps {
    initialData?: Partial<AgencyProfile>;
    onCancel?: () => void;
    isUpdateMode?: boolean;
    targetUid?: string;
    mode?: 'agency' | 'company';
    // MOIPHONGVAN001
    isInviteMode?: boolean;
    inviteCandidateId?: string;
    // DANGDON001
    isPostJobMode?: boolean;
    postJobRedirectUrl?: string;
    // DANGKYDOANHNGHIEP001
    isRegisterCompanyMode?: boolean;
}

export const AgencyRegistrationForm = ({ initialData, onCancel, isUpdateMode, targetUid, mode = 'agency', isInviteMode = false, inviteCandidateId, isPostJobMode = false, postJobRedirectUrl, isRegisterCompanyMode = false }: AgencyRegistrationFormProps) => {
    const { user, updateUserLocally } = useAuth();
    const router = useRouter();
    const { toast } = useToast();
    const { language } = useLanguage();
    const t = agencyTranslations.registrationForm;

    const steps = useMemo(() => [
        { id: 1, title: t.steps.company[language], icon: Building2 },
        { id: 2, title: t.steps.contact[language], icon: User },
        { id: 3, title: t.steps.capacity[language], icon: FileText },
    ], [language, t]);

    const industries = useMemo(() => {
        const fullList = allIndustries.map(ind => {
            const translation = (agencyTranslations.candidateSearch.industryNames as any)[ind.name];
            const localizedName = translation ? translation[language] || ind.name : ind.name;
            return { id: ind.slug, name: localizedName, rawName: ind.name };
        });

        // Luôn đảm bảo "Khác" nằm cuối
        return [...fullList, { id: 'other', name: t.industries.other[language], rawName: 'Khác' }];
    }, [language, t]);

    const [currentStep, setCurrentStep] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [isExpanded, setIsExpanded] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [showInviteReturnDialog, setShowInviteReturnDialog] = useState(false);
    const [showPostJobReturnDialog, setShowPostJobReturnDialog] = useState(false);

    const onUpdateProfile = async (updates: Partial<AgencyProfile>) => {
        setFormData(prev => ({ ...prev, ...updates }));
        
        // Instant sync to AuthContext only if editing self
        const isEditingSelf = !targetUid || targetUid === user?.uid;
        if (isEditingSelf) {
            updateUserLocally({
                agencyProfile: updates as any
            });
        }
        return true;
    };

    const {
        isSubmitting: isImageSubmitting,
        uploadProgress,
        isCropperOpen,
        setIsCropperOpen,
        croppingImage,
        croppingField,
        isHistoryOpen,
        setIsHistoryOpen,
        historyField,
        onFileSelected,
        handleRecrop,
        handleOpenHistory,
        handleCropComplete,
        handleHistorySelect,
        handleHistoryDelete,
        getOriginalImageUrl,
        getImageUrl
    } = useAgencyImageUpload({
        uid: user?.uid,
        language,
        onUpdate: onUpdateProfile
    });

    const combinedSubmitting = isSubmitting || isImageSubmitting;

    const [lightboxState, setLightboxState] = useState<{isOpen: boolean; images: (string | S3File)[]; currentIndex: number}>({
        isOpen: false,
        images: [],
        currentIndex: 0
    });

    const [docUploadProgress, setDocUploadProgress] = useState<Record<string, number>>({});

    const openLightbox = (images: (string | S3File)[], index: number = 0) => {
        setLightboxState({ isOpen: true, images, currentIndex: index });
    };

    const modalT = (key: string): string => {
        const imageActions = agencyTranslations.imageActions;
        const titles = {
            companyLogo: { vi: 'Logo Công ty', bn: 'কোম্পানির লোগো' },
            representativePhoto: { vi: 'Ảnh Giám đốc', bn: 'ব্যবস্থাপনা পরিচালকের ছবি' }
        };
        const activeType = croppingField || historyField;
        const currentTitle = activeType ? (titles[activeType as keyof typeof titles] || { vi: 'Ảnh', bn: 'ছবি' }) : { vi: 'Ảnh', bn: 'ছবি' };

        const mapping: Record<string, string> = {
            // For AvatarHistoryModal
            avatarHistoryTitle: language === 'vi' ? `Lịch sử ${currentTitle.vi}` : (language === 'bn' ? `${currentTitle.bn} ইতিহাস` : `History`),
            currentLabel: imageActions.currentLabel[language],
            delete: imageActions.delete[language],
            close: imageActions.close[language],
            
            // For AvatarCropperModal
            cropAvatarTitle: language === 'vi' ? `Cắt ${currentTitle.vi}` : (language === 'bn' ? `${currentTitle.bn} ক্রপ করুন` : `Crop Image`),
            save: imageActions.save[language],
            cancel: imageActions.cancel[language],
            cropErrorTitle: imageActions.cropErrorTitle[language],
            cropErrorDesc: imageActions.cropErrorDesc[language]
        };
        return mapping[key] || key;
    };


    const validateField = (name: string, value: string) => {
        return true;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        validateField(name, value);
        setFormData(prev => ({ ...prev, [name]: value }));

        // Instant sync for fields that affect Header (Name & Role Status) - Only if editing self
        const headerFields = ['managingDirector', 'companyName', 'officialEmail', 'officeAddress'];
        const isEditingSelf = !targetUid || targetUid === user?.uid;
        if (isEditingSelf && headerFields.includes(name)) {
            updateUserLocally({
                agencyProfile: { [name]: value } as any
            });
        }
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (!PHONE_CHAR_REGEX.test(value)) return;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name: string, value: string) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Sync to AuthContext if needed - Only if editing self
        const headerFields = ['managingDirector', 'companyName', 'officialEmail', 'officeAddress'];
        const isEditingSelf = !targetUid || targetUid === user?.uid;
        if (isEditingSelf && headerFields.includes(name)) {
            updateUserLocally({
                agencyProfile: { [name]: value } as any
            });
        }
    };

    const scrollToError = () => {
        const firstErrorField = Object.entries(fieldErrors).find(([_, hasError]) => hasError)?.[0];
        if (firstErrorField) {
            const element = document.getElementById(firstErrorField);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.focus();
                // Add a brief highlight effect
                element.classList.add('ring-2', 'ring-red-500', 'ring-offset-2');
                setTimeout(() => {
                    element.classList.remove('ring-2', 'ring-red-500', 'ring-offset-2');
                }, 2000);
            }
        }
    };

    const nextStep = () => {
        // Check for errors in the current step
        const step1Fields = ['companyName'];
        const step2Fields = ['officeAddress', 'managingDirector', 'representativeTitle', 'representativeBio'];
        
        const currentStepFields = currentStep === 1 ? step1Fields : currentStep === 2 ? step2Fields : [];
        const hasErrors = currentStepFields.some(field => fieldErrors[field]);

        if (hasErrors) {
            setShowErrorDialog(true);
            return;
        }

        if (currentStep < 3) setCurrentStep(prev => prev + 1);
    };

    const prevStep = () => {
        if (currentStep > 1) setCurrentStep(prev => prev - 1);
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, field: string, isMultiple = false) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const fileArray = Array.from(files) as File[];

        // If multiple, check limit
        if (isMultiple) {
            const currentImages = ((formData as any)[field] as (string | S3File)[]) || [];
            if (currentImages.length + fileArray.length > 6) {
                toast({
                    variant: 'destructive',
                    title: t.toasts.error.title[language],
                    description: (t.toasts.error as any).maxImages[language],
                });
                return;
            }
        }

        setIsSubmitting(true);
        try {
            for (const file of fileArray) {
                const progressKey = isMultiple ? `gallery-${file.name}-${file.size}` : field;
                setDocUploadProgress(prev => ({ ...prev, [progressKey]: 0 }));

                // Compression options (Ref: Candidate Profile)
                const options = {
                    maxSizeMB: 1,
                    maxWidthOrHeight: 1200,
                    useWebWorker: true,
                };

                const compressedFile = await imageCompression(file, options);

                // Get presigned URL - Use temp/ prefix as per Candidate flow
                const fileKey = `temp/doi-tac/${user?.uid}/${field}/${Date.now()}-${file.name}`;
                const res = await getPresignedUploadUrl({
                    key: fileKey,
                    contentType: file.type
                });

                if (res.success && res.signedUrl) {
                    // Upload to S3 using XMLHttpRequest for progress tracking
                    await new Promise((resolve, reject) => {
                        const xhr = new XMLHttpRequest();
                        xhr.open('PUT', res.signedUrl!);
                        xhr.setRequestHeader('Content-Type', file.type);

                        xhr.upload.onprogress = (event) => {
                            if (event.lengthComputable) {
                                const percentCompleted = Math.round((event.loaded * 100) / event.total);
                                setDocUploadProgress(prev => ({ ...prev, [progressKey]: percentCompleted }));
                            }
                        };

                        xhr.onload = () => {
                            if (xhr.status >= 200 && xhr.status < 300) {
                                resolve(xhr.response);
                            } else {
                                reject(new Error(`Upload failed with status ${xhr.status}`));
                            }
                        };

                        xhr.onerror = () => reject(new Error('Upload failed'));
                        xhr.send(compressedFile);
                    });

                    const publicUrl = res.signedUrl.split('?')[0];
                    const s3File = { url: publicUrl, key: fileKey };

                    if (isMultiple) {
                        setFormData(prev => {
                            const prevArray = Array.isArray((prev as any)[field]) ? (prev as any)[field] : ((prev as Record<string, any>)[field] ? [(prev as Record<string, any>)[field]] : []);
                            return {
                            ...prev,
                            [field]: [...prevArray, s3File]
                            };
                        });
                    } else {
                        // FOR NON-MULTIPLES (History handled in handleCropComplete, but also here for safety)
                        const historyField = field === 'representativePhoto' ? 'representativePhotoHistory' : 
                                           field === 'companyLogo' ? 'companyLogoHistory' : null;
                        
                        setFormData(prev => {
                            const updates: any = { [field]: s3File };
                            if (historyField) {
                                const currentHistory = (prev as any)[historyField] || [];
                                updates[historyField] = [publicUrl, ...currentHistory.filter((u: any) => u !== publicUrl)].slice(0, 10);
                            }
                            return { ...prev, ...updates };
                        });
                    }
                }

                // Clear progress after short delay
                setTimeout(() => {
                    setDocUploadProgress(prev => {
                        const newProgress = { ...prev };
                        delete newProgress[progressKey];
                        return newProgress;
                    });
                }, 1000);
            }

            toast({
                title: language === 'vi' ? "Tải lên thành công" : "Upload successful",
                className: 'bg-green-500 text-white',
            });
        } catch (error) {
            console.error('Upload error:', error);
            toast({
                variant: 'destructive',
                title: language === 'vi' ? "Lỗi upload" : "Upload error",
                description: language === 'vi' ? "Không thể tải ảnh lên. Vui lòng thử lại." : "Could not upload image. Please try again.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const removeImage = (index: number, field: string = 'actualImages') => {
        setFormData(prev => ({
            ...prev,
            [field]: ((prev as any)[field] || []).filter((_: any, i: number) => i !== index)
        }));
    };

    const moveImage = (index: number, direction: 'left' | 'right', field: string = 'actualImages') => {
        const images = [...((formData as any)[field] || [])];
        if (direction === 'left' && index > 0) {
            [images[index], images[index - 1]] = [images[index - 1], images[index]];
        } else if (direction === 'right' && index < images.length - 1) {
            [images[index], images[index + 1]] = [images[index + 1], images[index]];
        }
        setFormData(prev => ({ ...prev, [field]: images }));
    };

    const handleSubmit = async () => {
        // Final validation check
        const targetFields = ['companyName', 'officeAddress', 'managingDirector', 'representativeTitle', 'representativeBio'];
        const hasErrors = targetFields.some(field => fieldErrors[field]);

        if (hasErrors) {
            setShowErrorDialog(true);
            return;
        }

        const uid = targetUid || user?.uid;
        if (!uid) return;

        setIsSubmitting(true);
        try {
            const res = await submitAgencyRegistration(uid, formData as AgencyProfile, mode);
            if (res.success) {
                toast({
                    title: t.toasts.success.title[language],
                    description: t.toasts.success.desc[language],
                    className: 'bg-green-500 text-white',
                });
                if (isUpdateMode) {
                    if (isInviteMode && inviteCandidateId) {
                        router.push(`/ung-vien?autoInvite=${inviteCandidateId}`);
                    } else if (isPostJobMode && postJobRedirectUrl) {
                        router.push(postJobRedirectUrl);
                    } else if (isRegisterCompanyMode) {
                        // Luồng DANGKYDOANHNGHIEP001: Về trang profile kèm cờ báo hiệu để hiện popup chúc mừng
                        router.push('/ho-so-cua-toi?isNewOrg=true');
                    } else if (targetUid) {
                        router.push(`/cac-dai-ly/${targetUid}`);
                    } else {
                        router.push(mode === 'company' ? '/ho-so-cua-toi' : '/doi-tac/ho-so');
                    }
                } else {
                    window.location.reload();
                }
            } else {
                toast({
                    variant: 'destructive',
                    title: t.toasts.error.title[language],
                    description: res.error || t.toasts.error.desc[language],
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const defaultValues = useMemo(() => ({
        companyName: '',
        rlNumber: '',
        establishedYear: '',
        tradeLicense: '',
        officeAddress: '',
        officialEmail: user?.email || '',
        officialPhone: '',
        websiteUrl: '',
        whatsapp: '',
        zalo: '',
        facebookUrl: '',
        managingDirector: '',
        mdPhone: '',
        representativeTitle: '',
        representativeBio: '',
        representativeName: user?.fullName || '',
        monthlySupplyCapacity: '0-50',
        mainIndustries: [],
        otherIndustries: '',
        rlLicensePhoto: [],
        officePhoto: '',
        companyLogo: '',
        representativePhoto: undefined,
        actualImages: [],
    }), [user]);

    const [formData, setFormData] = useState<Partial<AgencyProfile>>(() => ({
        ...defaultValues,
        ...initialData
    }));

    // Sync formData when initialData changes (e.g. after async load)
    React.useEffect(() => {
        if (initialData && Object.keys(initialData).length > 0) {
            const normalizedData = { ...initialData };
            
            // Normalize image fields to always be arrays
            const arrayFields: (keyof AgencyProfile)[] = ['rlLicensePhoto', 'actualImages'];
            arrayFields.forEach(field => {
                const value = normalizedData[field];
                if (value && !Array.isArray(value)) {
                    (normalizedData as any)[field] = [value];
                } else if (!value) {
                    (normalizedData as any)[field] = [];
                }
            });

            setFormData(prev => ({
                ...defaultValues,
                ...prev,
                ...normalizedData
            }));
        }
    }, [initialData, defaultValues]);
    const renderImageGrid = (field: 'rlLicensePhoto' | 'actualImages', maxImages: number = 6) => {
        const rawImages = formData[field];
        const images = Array.isArray(rawImages) 
            ? (rawImages as (string | S3File)[]) 
            : (rawImages ? [rawImages as unknown as (string | S3File)] : []);
        const isGallery = field === 'actualImages';
        const label = isGallery ? t.labels.actualImages[language] : t.labels.rlLicensePhoto[language];
        const uploadLabel = isGallery ? t.placeholders.uploadGallery[language] : t.placeholders.uploadRL[language];
        const primaryLabel = t.placeholders.primary[language];

        return (
            <div className={`space-y-4 pt-4 ${isGallery ? 'border-t border-slate-100' : ''}`}>
                <div className="flex items-center justify-between">
                    <Label className="font-bold text-lg">{label} {field === 'rlLicensePhoto' && <span className="text-[#F2B92A]">*</span>}</Label>
                    <span className="text-xs font-bold text-slate-400">{images.length}/{maxImages}</span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {images.map((item, idx) => (
                        <div key={idx} className="relative aspect-square rounded-xl overflow-hidden group shadow-sm border border-slate-100">
                            <img src={typeof item === 'string' ? item : item.url} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                                <div className="flex gap-1">
                                    <Button
                                        size="icon"
                                        variant="secondary"
                                        className="h-8 w-8 rounded-full"
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); moveImage(idx, 'left', field); }}
                                        disabled={idx === 0}
                                    >
                                        <ArrowLeftIcon className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        size="icon"
                                        variant="secondary"
                                        className="h-8 w-8 rounded-full bg-white text-blue-600 hover:bg-blue-50"
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); openLightbox(images as any, idx); }}
                                    >
                                        <Search className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        size="icon"
                                        variant="secondary"
                                        className="h-8 w-8 rounded-full"
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); moveImage(idx, 'right', field); }}
                                        disabled={idx === images.length - 1}
                                    >
                                        <ArrowRightIcon className="h-4 w-4" />
                                    </Button>
                                </div>
                                <Button
                                    size="icon"
                                    variant="destructive"
                                    className="h-8 w-8 rounded-full bg-red-500 hover:bg-red-600 text-white border-0"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeImage(idx, field); }}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                            {idx === 0 && (
                                <div className="absolute top-2 left-2 bg-[#F2B92A] text-white text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-tighter shadow-sm blur-none">
                                    {primaryLabel}
                                </div>
                            )}
                        </div>
                    ))}

                    {images.length < maxImages && (
                        <label
                            htmlFor={`${field}Input`}
                            className="aspect-square rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-100 hover:border-[#F2B92A] transition-all group"
                        >
                            <ImagePlus className="w-8 h-8 text-slate-300 group-hover:text-[#F2B92A] transition-colors mb-2" />
                            <span className="text-[10px] font-bold text-slate-400 group-hover:text-[#F2B92A] uppercase tracking-tighter text-center">{uploadLabel}</span>
                            <input
                                type="file"
                                id={`${field}Input`}
                                className="hidden"
                                accept="image/*"
                                multiple
                                onChange={(e) => handleImageUpload(e, field, true)}
                            />
                        </label>
                    )}

                    {/* Uploading placeholders */}
                    {Object.entries(uploadProgress)
                        .filter(([key]) => key.startsWith(`gallery-`) && uploadProgress[key] !== undefined)
                        .map(([key, progress]) => (
                            <div key={key} className="aspect-square rounded-xl border border-dashed border-[#F2B92A] bg-[#FFFBEB] flex flex-col items-center justify-center p-3 animate-pulse">
                                <Loader2 className="w-6 h-6 text-[#F2B92A] animate-spin mb-2" />
                                <div className="w-full space-y-1.5">
                                    <div className="flex justify-between text-[9px] font-black text-[#F2B92A] uppercase tracking-tighter">
                                        <span>{t.placeholders.loading[language]}</span>
                                        <span>{progress}%</span>
                                    </div>
                                    <Progress value={progress} className="h-1 bg-white" />
                                </div>
                            </div>
                        ))
                    }
                </div>
                <p className="text-[10px] text-slate-400 flex items-center gap-1.5 italic">
                    <AlertCircle className="w-3 h-3 block" />
                    {t.placeholders.dragToSort[language]}
                </p>
            </div>
        );
    };

    return (
        <div className="max-w-4xl mx-auto py-10 px-4">
            <div className="mb-12 text-center">
                <h1 className="text-3xl font-bold font-headline text-[#0D8DC8] mb-4">
                    {isRegisterCompanyMode 
                        ? 'Hoàn tất đăng ký doanh nghiệp tuyển dụng'
                        : (mode === 'company'
                            ? 'Đăng ký doanh nghiệp tuyển dụng lao động'
                            : t.header.title[language])
                    }
                </h1>
                <p className="text-slate-500 max-w-2xl mx-auto">
                    {t.header.desc[language]}
                </p>
            </div>

            {/* MOIPHONGVAN001: Banner */}
            {isInviteMode && (
                <div className="bg-orange-50 border border-orange-200 text-orange-800 px-4 py-3 rounded-xl mb-8 flex items-start gap-3 animate-in fade-in slide-in-from-top-4">
                    <AlertCircle className="w-5 h-5 mt-0.5 shrink-0 text-orange-500" />
                    <div className="text-sm">
                        <span className="font-bold block mb-1">Mời phỏng vấn nhanh</span>
                        Bạn chỉ cần điền <b>Tên pháp nhân công ty</b> là đã đủ điều kiện để gửi lời mời phỏng vấn. Các thông tin khác có thể bổ sung sau.
                    </div>
                </div>
            )}

            {/* DANGDON001: Banner */}
            {isPostJobMode && (
                <div className="bg-orange-50 border border-orange-200 text-orange-800 px-4 py-3 rounded-xl mb-8 flex items-start gap-3 animate-in fade-in slide-in-from-top-4">
                    <AlertCircle className="w-5 h-5 mt-0.5 shrink-0 text-orange-500" />
                    <div className="text-sm">
                        <span className="font-bold block mb-1">Đăng tin tuyển dụng</span>
                        Bạn đang trong quy trình Đăng tin tuyển dụng. Vui lòng hoàn thành mục <b>Tên pháp nhân công ty</b> bên dưới để tiến hành đăng tin.
                    </div>
                </div>
            )}

            {/* DANGKYDOANHNGHIEP001: Banner */}
            {isRegisterCompanyMode && (
                <div className="bg-[#19A6DF]/10 border border-[#19A6DF]/30 text-[#0D8DC8] px-4 py-3 rounded-xl mb-8 flex items-start gap-3 animate-in fade-in slide-in-from-top-4">
                    <Info className="w-5 h-5 mt-0.5 shrink-0 text-[#19A6DF]" />
                    <div className="text-sm">
                        <span className="font-bold block mb-1">Chào mừng bạn mới đến với HelloJob! 🎉</span>
                        Vui lòng điền đầy đủ các thông tin pháp nhân và ngành nghề bên dưới để chúng tôi hỗ trợ bạn tuyển dụng lao động Bangladesh hiệu quả nhất.
                    </div>
                </div>
            )}

            {/* Stepper Header */}
            <div className="flex justify-between mb-8 relative">
                <div className="absolute top-1/2 left-0 w-full h-0.5 bg-slate-100 -translate-y-1/2 z-0" />
                {steps.map((step) => {
                    const StepIcon = step.icon;
                    const isActive = currentStep === step.id;
                    const isCompleted = currentStep > step.id;

                    return (
                        <div key={step.id} className="relative z-10 flex flex-col items-center gap-1 md:gap-2">
                            <div className={cn(
                                "w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300 border-[3px] md:border-4",
                                isActive ? "bg-[#F2B92A] border-orange-50 text-white scale-105 md:scale-110" :
                                    isCompleted ? "bg-[#AFC536] border-green-50 text-white" :
                                        "bg-white border-slate-100 text-slate-400"
                            )}>
                                {isCompleted ? <Check className="h-5 w-5 md:h-6 md:w-6" /> : <StepIcon className="h-5 w-5 md:h-6 md:w-6" />}
                            </div>
                            <span className={cn(
                                "text-[10px] md:text-sm font-bold transition-colors whitespace-nowrap",
                                isActive ? "text-[#F2B92A]" : isCompleted ? "text-[#AFC536]" : "text-slate-400"
                            )}>
                                {step.title}
                            </span>
                        </div>
                    );
                })}
            </div>

            <Card className="border-slate-100 shadow-xl overflow-hidden rounded-2xl">
                <CardContent className="p-4 sm:p-6 md:p-8">
                    {/* Step 1: Company Profile */}
                    {currentStep === 1 && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label htmlFor="companyName" className="font-bold">{t.labels.companyName[language]} <span className="text-[#F2B92A]">*</span></Label>
                                    <div className="relative">
                                        <Building2 className="absolute left-3 top-3 h-4 w-4 text-[#19A6DF]" />
                                        <Input
                                            id="companyName"
                                            name="companyName"
                                            placeholder={t.placeholders.companyName[language]}
                                            className={cn(
                                                "pl-10 focus-visible:ring-[#F2B92A] transition-all",
                                                fieldErrors.companyName && "border-red-500 ring-1 ring-red-500",
                                                (isInviteMode || isPostJobMode) && "ring-2 ring-orange-400 ring-offset-2 border-orange-400 bg-orange-50/30"
                                            )}
                                            value={formData.companyName}
                                            onChange={handleInputChange}
                                            onBlur={() => {
                                                if (isInviteMode && formData.companyName && formData.companyName.trim().length > 0) {
                                                    setShowInviteReturnDialog(true);
                                                }
                                                if (isPostJobMode && formData.companyName && formData.companyName.trim().length > 0) {
                                                    setShowPostJobReturnDialog(true);
                                                }
                                            }}
                                        />

                                        {/* MOIPHONGVAN001: Dialog hỏi quay về khi Blur */}
                                        <AlertDialog open={showInviteReturnDialog} onOpenChange={setShowInviteReturnDialog}>
                                            <AlertDialogContent>
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle className="flex items-center gap-2 text-slate-900">
                                                        <CheckCircle2 className="w-5 h-5 text-[#19A6DF]" />
                                                        Thông tin đã sẵn sàng!
                                                    </AlertDialogTitle>
                                                    <AlertDialogDescription className="text-slate-600 leading-relaxed">
                                                        Bạn đã cập nhật Tên pháp nhân thành công. Hệ thống hiện đã đủ điều kiện để thực hiện lời mời phỏng vấn ngay lập tức. Bạn muốn <strong>quay lại mời ứng viên</strong> ngay bây giờ hay tiếp tục hoàn thiện các mục khác trong hồ sơ doanh nghiệp?
                                                    </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel onClick={() => setShowInviteReturnDialog(false)}>Tiếp tục điền hồ sơ</AlertDialogCancel>
                                                    <AlertDialogAction
                                                        className="bg-[#19A6DF] hover:bg-[#158bbd] text-white"
                                                        onClick={() => {
                                                            setShowInviteReturnDialog(false);
                                                            // Trigger save immediately then redirect
                                                            handleSubmit();
                                                        }}
                                                    >
                                                        Mời phỏng vấn ngay
                                                    </AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>

                                        {/* DANGDON001: Dialog hỏi quay về khi Blur */}
                                        <AlertDialog open={showPostJobReturnDialog} onOpenChange={setShowPostJobReturnDialog}>
                                            <AlertDialogContent>
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle className="flex items-center gap-2 text-slate-900">
                                                        <CheckCircle2 className="w-5 h-5 text-[#19A6DF]" />
                                                        Thông tin đã sẵn sàng!
                                                    </AlertDialogTitle>
                                                    <AlertDialogDescription className="text-slate-600 leading-relaxed">
                                                        Bạn đã cập nhật Tên pháp nhân thành công. Hệ thống đã đủ điều kiện để đăng việc làm. Bạn muốn <strong>đi đến màn hình đăng việc làm</strong> ngay bây giờ hay tiếp tục hoàn thiện hồ sơ?
                                                    </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel onClick={() => setShowPostJobReturnDialog(false)}>Tiếp tục điền hồ sơ</AlertDialogCancel>
                                                    <AlertDialogAction
                                                        className="bg-[#19A6DF] hover:bg-[#158bbd] text-white"
                                                        onClick={() => {
                                                            setShowPostJobReturnDialog(false);
                                                            // Trigger save immediately then redirect
                                                            handleSubmit();
                                                        }}
                                                    >
                                                        Đăng việc làm ngay
                                                    </AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>

                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="rlNumber" className="font-bold">{t.labels.rlNumber[language]} <span className="text-[#F2B92A]">*</span></Label>
                                    <div className="relative">
                                        <FileText className="absolute left-3 top-3 h-4 w-4 text-[#19A6DF]" />
                                        <Input
                                            id="rlNumber"
                                            name="rlNumber"
                                            placeholder={t.placeholders.rlNumber[language]}
                                            className="pl-10 font-body focus-visible:ring-[#F2B92A]"
                                            value={formData.rlNumber}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="establishedYear" className="font-bold">{t.labels.establishedYear[language]}</Label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-3 h-4 w-4 text-[#19A6DF]" />
                                        <Input
                                            id="establishedYear"
                                            name="establishedYear"
                                            placeholder={t.placeholders.establishedYear[language]}
                                            className="pl-10 focus-visible:ring-[#F2B92A]"
                                            value={formData.establishedYear}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="tradeLicense" className="font-bold">{t.labels.tradeLicense[language]}</Label>
                                    <Input
                                        id="tradeLicense"
                                        name="tradeLicense"
                                        placeholder={t.placeholders.tradeLicense[language]}
                                        className="focus-visible:ring-[#F2B92A]"
                                        value={formData.tradeLicense}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Contact & Representative */}
                    {currentStep === 2 && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="space-y-2">
                                <Label htmlFor="officeAddress" className="font-bold">{t.labels.officeAddress[language]} <span className="text-[#F2B92A]">*</span></Label>
                                <div className="relative">
                                    <MapPin className="absolute left-3 top-3 h-4 w-4 text-[#19A6DF]" />
                                    <Input
                                        id="officeAddress"
                                        name="officeAddress"
                                        placeholder={t.placeholders.officeAddress[language]}
                                        className={cn(
                                            "pl-10 focus-visible:ring-[#F2B92A] transition-all",
                                            fieldErrors.officeAddress && "border-red-500 ring-1 ring-red-500"
                                        )}
                                        value={formData.officeAddress}
                                        onChange={handleInputChange}
                                    />

                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label htmlFor="officialEmail" className="font-bold">{t.labels.officialEmail[language]}</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-3 h-4 w-4 text-[#19A6DF]" />
                                        <Input id="officialEmail" name="officialEmail" placeholder={t.placeholders.officialEmail[language]} className="pl-10 focus-visible:ring-[#F2B92A]" value={formData.officialEmail} onChange={handleInputChange} />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="officialPhone" className="font-bold">{t.labels.officialPhone[language]} <span className="text-[#F2B92A]">*</span></Label>
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-3 h-4 w-4 text-[#19A6DF]" />
                                        <Input id="officialPhone" name="officialPhone" type="tel" inputMode="tel" placeholder={t.placeholders.officialPhone[language]} className="pl-10 focus-visible:ring-[#F2B92A]" value={formData.officialPhone} onChange={handlePhoneChange} />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="managingDirector" className="font-bold">{t.labels.managingDirector[language]}</Label>
                                    <Input 
                                        id="managingDirector" 
                                        name="managingDirector" 
                                        placeholder={t.placeholders.managingDirector[language]} 
                                        className={cn(
                                            "focus-visible:ring-[#F2B92A] transition-all",
                                            fieldErrors.managingDirector && "border-red-500 ring-1 ring-red-500"
                                        )} 
                                        value={formData.managingDirector} 
                                        onChange={handleInputChange} 
                                    />

                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="mdPhone" className="font-bold">{t.labels.mdPhone[language]}</Label>
                                    <Input id="mdPhone" name="mdPhone" type="tel" inputMode="tel" placeholder={t.placeholders.mdPhone[language]} className="focus-visible:ring-[#F2B92A]" value={formData.mdPhone} onChange={handlePhoneChange} />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="representativeTitle" className="font-bold">{(t.labels as any).representativeTitle[language]}</Label>
                                    <Input 
                                        id="representativeTitle" 
                                        name="representativeTitle" 
                                        placeholder={t.placeholders.representativeTitle[language]} 
                                        className={cn(
                                            "focus-visible:ring-[#F2B92A] transition-all",
                                            fieldErrors.representativeTitle && "border-red-500 ring-1 ring-red-500"
                                        )} 
                                        value={formData.representativeTitle} 
                                        onChange={handleInputChange} 
                                    />

                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <Label htmlFor="representativeBio" className="font-bold">{(t.labels as any).representativeBio[language]}</Label>
                                    <Textarea
                                        id="representativeBio"
                                        name="representativeBio"
                                        placeholder={t.placeholders.representativeBio[language]}
                                        className={cn(
                                            "focus-visible:ring-[#F2B92A] min-h-[100px] transition-all",
                                            fieldErrors.representativeBio && "border-red-500 ring-1 ring-red-500"
                                        )}
                                        value={formData.representativeBio}
                                        onChange={handleInputChange}
                                    />

                                </div>
                                <div className="space-y-2 md:col-span-2">
                                <AgencySharedImageField
                                    variant="box"
                                    field="representativePhoto"
                                    profileData={formData}
                                    isImageSubmitting={isImageSubmitting}
                                    uploadProgress={uploadProgress.representativePhoto}
                                    getImageUrl={getImageUrl}
                                    getOriginalImageUrl={getOriginalImageUrl}
                                    onFileSelected={onFileSelected}
                                    handleRecrop={handleRecrop}
                                    handleOpenHistory={handleOpenHistory}
                                    openLightbox={openLightbox}
                                    language={language}
                                    boxLabel={t.labels.representativePhoto[language]}
                                    boxSquareLabel={(t.labels as any).squareRequired[language]}
                                    boxPlaceholderTitle={t.placeholders.uploadAvatar[language]}
                                    boxPlaceholderDesc={t.placeholders.avatarAdvice[language]}
                                />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 border-t border-slate-50">
                                <div className="space-y-2">
                                    <Label htmlFor="whatsapp" className="font-bold flex items-center gap-2">
                                        <img src="/img/BANGLADESH/TRANGDOITAC/WhatsApp.svg" className="w-4 h-4" />
                                        {t.labels.whatsapp[language]}
                                    </Label>
                                    <Input id="whatsapp" name="whatsapp" type="tel" inputMode="tel" placeholder={t.placeholders.socialPlaceholder[language]} className="focus-visible:ring-[#F2B92A]" value={formData.whatsapp} onChange={handlePhoneChange} />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="zalo" className="font-bold flex items-center gap-2">
                                        <img src="/img/Zalo.svg" className="w-4 h-4" />
                                        {t.labels.zalo[language]}
                                    </Label>
                                    <Input id="zalo" name="zalo" placeholder={t.placeholders.zaloId[language]} className="focus-visible:ring-[#F2B92A]" value={formData.zalo} onChange={handleInputChange} />
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <Label htmlFor="facebookUrl" className="font-bold flex items-center gap-2 text-[#1877F2]">
                                        <Facebook className="w-4 h-4" />
                                        {t.labels.facebook[language]}
                                    </Label>
                                    <Input id="facebookUrl" name="facebookUrl" placeholder={t.placeholders.facebookPlaceholder[language]} className="focus-visible:ring-[#F2B92A]" value={formData.facebookUrl} onChange={handleInputChange} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Năng lực & Tài liệu */}
                    {currentStep === 3 && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label htmlFor="capacity" className="font-bold">
                                        {mode === 'company'
                                            ? 'Khả năng tiếp nhận (Người/tháng)'
                                            : t.labels.supplyCapacity[language]
                                        }
                                    </Label>
                                    <Select
                                        value={formData.monthlySupplyCapacity}
                                        onValueChange={(val) => handleSelectChange('monthlySupplyCapacity', val)}
                                    >
                                        <SelectTrigger id="capacity" className="focus:ring-[#F2B92A]">
                                            <SelectValue placeholder={t.placeholders.selectLevel[language]} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="0-50">{t.capacityOptions.under50[language]}</SelectItem>
                                            <SelectItem value="50-100">{t.capacityOptions.range50_100[language]}</SelectItem>
                                            <SelectItem value="100-200">{t.capacityOptions.range100_200[language]}</SelectItem>
                                            <SelectItem value="200+">{t.capacityOptions.over200[language]}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <Label className="font-bold text-[#0D8DC8] flex justify-between items-center">
                                        <span>{t.labels.mainIndustries[language]}</span>
                                        <span className="text-[10px] text-slate-400">{(formData.mainIndustries || []).length} {t.placeholders.selectedCount[language]}</span>
                                    </Label>
                                    
                                    {/* Search Bar for Industries */}
                                    <div className="relative mb-3">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                                        <Input 
                                            placeholder={t.placeholders.searchIndustries[language]}
                                            className="h-9 pl-9 text-xs focus-visible:ring-[#19A6DF]/30"
                                            value={searchQuery}
                                            onChange={(e) => {
                                                setSearchQuery(e.target.value);
                                                if (e.target.value) setIsExpanded(true);
                                            }}
                                        />
                                        {searchQuery && (
                                            <button 
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                                onClick={() => setSearchQuery('')}
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        )}
                                    </div>

                                    <div className="flex flex-wrap gap-2 pt-1">
                                        {industries
                                            .filter(ind => {
                                                if (!searchQuery) return true;
                                                return ind.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                                                       ind.rawName.toLowerCase().includes(searchQuery.toLowerCase());
                                            })
                                            .slice(0, (isExpanded || searchQuery) ? undefined : 8)
                                            .map(ind => {
                                                const isSelected = formData.mainIndustries?.includes(ind.id);
                                                return (
                                                    <Button 
                                                        key={ind.id} 
                                                        variant={isSelected ? 'default' : 'outline'}
                                                        size="sm"
                                                        className={cn(
                                                            "rounded-full transition-all text-xs h-8",
                                                            isSelected 
                                                                ? "bg-[#F2B92A] hover:bg-[#F2B92A]/90 text-white border-[#F2B92A] shadow-sm" 
                                                                : "border-slate-200 hover:border-[#19A6DF] hover:text-[#19A6DF] bg-white"
                                                        )}
                                                        onClick={() => {
                                                            const current = formData.mainIndustries || [];
                                                            const updated = current.includes(ind.id) 
                                                                ? current.filter(i => i !== ind.id)
                                                                : [...current, ind.id];
                                                            setFormData(prev => ({ ...prev, mainIndustries: updated }));
                                                        }}
                                                    >
                                                        {ind.name}
                                                    </Button>
                                                );
                                            })}
                                    </div>
                                    
                                    {!searchQuery && industries.length > 8 && (
                                        <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="mt-2 text-[11px] font-bold text-[#19A6DF] hover:bg-blue-50 px-2 h-7"
                                            onClick={() => setIsExpanded(!isExpanded)}
                                        >
                                            {isExpanded 
                                                ? t.placeholders.showLess[language]
                                                : `${t.placeholders.showMore[language]} +${industries.length - 8}`
                                            }
                                        </Button>
                                    )}

                                    {formData.mainIndustries?.includes('other') && (
                                        <div className="pt-3 animate-in fade-in zoom-in-95 duration-300">
                                            <Label className="text-[11px] font-bold text-slate-500 mb-1 block">{t.placeholders.otherIndustryDetail[language]}</Label>
                                            <Input 
                                                name="otherIndustries" 
                                                placeholder={t.labels.otherIndustry[language]} 
                                                className="focus-visible:ring-[#F2B92A] h-9 text-sm"
                                                value={formData.otherIndustries}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                                <AgencySharedImageField
                                    variant="box"
                                    field="companyLogo"
                                    profileData={formData}
                                    isImageSubmitting={isImageSubmitting}
                                    uploadProgress={uploadProgress.companyLogo}
                                    getImageUrl={getImageUrl}
                                    getOriginalImageUrl={getOriginalImageUrl}
                                    onFileSelected={onFileSelected}
                                    handleRecrop={handleRecrop}
                                    handleOpenHistory={handleOpenHistory}
                                    openLightbox={openLightbox}
                                    language={language}
                                    boxLabel={t.labels.companyLogo[language]}
                                    boxSquareLabel={t.placeholders.squareLabel[language]}
                                    boxPlaceholderTitle={t.placeholders.uploadLogo[language]}
                                    boxPlaceholderDesc={t.placeholders.logoResolutionAdvice[language]}
                                />
                                <div className="space-y-2 md:col-span-2">
                                    {renderImageGrid('rlLicensePhoto')}
                                </div>
                            </div>

                            {renderImageGrid('actualImages')}
                        </div>
                    )}

                    {/* Navigation Buttons */}
                    <div className="flex flex-col sm:flex-row justify-between items-center mt-8 md:mt-12 pt-6 md:pt-8 border-t border-slate-100 gap-4">
                        <div className="flex gap-2 md:gap-3 w-full sm:w-auto">
                            {currentStep > 1 ? (
                                <Button
                                    variant="ghost"
                                    className="gap-2 hover:text-[#0D8DC8] hover:bg-slate-50 flex-1 sm:flex-initial"
                                    onClick={prevStep}
                                >
                                    <ChevronLeft className="h-4 w-4" /> {t.buttons.back[language]}
                                </Button>
                            ) : onCancel ? (
                                <Button
                                    variant="outline"
                                    className="gap-2 border-slate-200 flex-1 sm:flex-initial"
                                    onClick={onCancel}
                                >
                                    <X className="h-4 w-4" /> {t.buttons.cancel[language]}
                                </Button>
                            ) : <div className="hidden sm:block" />}
                        </div>

                        {currentStep < 3 ? (
                            <Button className="bg-[#F2B92A] hover:bg-[#F2B92A]/90 text-white gap-2 px-8 shadow-md w-full sm:w-auto min-h-[44px]" onClick={nextStep}>
                                {t.buttons.next[language]} <ChevronRight className="h-4 w-4" />
                            </Button>
                        ) : (
                            <Button
                                className="bg-[#AFC536] hover:bg-[#AFC536]/90 text-white gap-2 px-8 font-bold shadow-md w-full sm:w-auto min-h-[44px]"
                                onClick={handleSubmit}
                                disabled={isSubmitting || isImageSubmitting}
                            >
                                {isSubmitting 
                                    ? <Loader2 className="h-4 w-4 animate-spin" /> 
                                    : (isRegisterCompanyMode ? "Xác nhận & Bắt đầu tuyển dụng" : t.buttons.submit[language])
                                }
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            <div className="mt-8 flex items-start gap-3 p-4 bg-orange-50 border border-[#F2B92A]/20 rounded-xl text-slate-800 text-sm">
                <AlertCircle className="h-5 w-5 shrink-0 text-[#F2B92A]" />
                <p>
                    {t.privacy[language]}
                </p>
            </div>

            {/* Image Modals */}
            <AvatarCropperModal
                isOpen={isCropperOpen}
                imageSrc={croppingImage}
                onClose={() => setIsCropperOpen(false)}
                onCropComplete={(file) => handleCropComplete(file, formData)}
                t={modalT}
            />

            <AvatarHistoryModal
                isOpen={isHistoryOpen}
                onClose={() => setIsHistoryOpen(false)}
                currentAvatarUrl={historyField ? getImageUrl(formData, historyField) || '' : ''}
                avatarHistory={historyField ? (formData as any)[`${historyField}History`] || [] : []}
                onSelectAvatar={handleHistorySelect}
                onDeleteAvatar={(url) => handleHistoryDelete(url, formData)}
                isProcessing={isImageSubmitting}
                t={modalT}
            />

            <ImageGalleryLightbox
                isOpen={lightboxState.isOpen}
                onClose={() => setLightboxState(prev => ({ ...prev, isOpen: false }))}
                images={lightboxState.images}
                currentIndex={lightboxState.currentIndex}
                onIndexChange={(idx) => setLightboxState(prev => ({ ...prev, currentIndex: idx }))}
            />


        </div>
    );
};
